import {
  AspectRatio,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  Image,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@chakra-ui/react';
import { fromUnixTime } from 'date-fns';
import { scroller } from 'react-scroll';
import { useTranslation } from 'next-i18next';

import Copy from '@components/Copy';
import { EventType } from '@lib/types';
import { getGoogleCalendarUrl } from '@lib/utils';

import AddToCalendarIcon from '../icons/add_to_calendar.svg';
import CalendarIcon from '../icons/calendar.svg';
import ShareIcon from '../icons/share.svg';
import { EVENT_FALLBACK_COVER_PHOTO_URL, ORIGIN } from '../../constants';
import { TEXT_COLOR } from '../../layouts/EventLayout';

import EventDateTime from './EventDateTime';
import EventStatus from './EventStatus';

type Props = {
  event: EventType;
};

const Hero = ({ event }: Props) => {
  const { t } = useTranslation(['common', 'event']);

  return (
    <Grid
      templateColumns={{
        base: '1fr',
        md: 'minmax(0, 1fr) 410px',
      }}
      columnGap={'60px'}
      position="relative"
    >
      <GridItem position={'relative'} left={{ base: '-16px', md: '0' }}>
        <AspectRatio
          // FB cover ratio
          ratio={1200 / 630}
          width={{ base: 'calc(100% + 32px)', md: '100%' }}
        >
          <Image
            src={event.coverPhoto || EVENT_FALLBACK_COVER_PHOTO_URL}
            objectFit="cover"
            boxShadow={{
              base: 'none',
              md: '4px 4px 15px 0px rgba(0, 0, 0, 0.1)',
            }}
            borderRadius={{ base: 0, md: '10px' }}
          />
        </AspectRatio>
      </GridItem>
      <GridItem>
        <Flex
          flexDirection="column"
          justifyContent="space-between"
          height="100%"
        >
          {/* Event details */}
          <Flex flexDirection="column" mt={{ base: '30px', md: '0' }}>
            <Heading
              as="h1"
              fontWeight="500"
              fontSize={{ base: '30px', md: '4xl' }}
              lineHeight="1.5"
            >
              {event.name}
            </Heading>
            <Flex alignItems="center" mt="2">
              <Flex shrink={0}>
                <CalendarIcon />
              </Flex>
              <EventDateTime
                startedAt={event.startedAt}
                endedAt={event.endedAt}
              />
            </Flex>
            <Flex
              alignItems="center"
              fontSize="15px"
              lineHeight="31px"
              color="#000"
              mt="2"
            >
              {t('event:Host by')}
              <Button
                color="brand.500"
                variant="link"
                ml="1"
                onClick={() => {
                  scroller.scrollTo('host', {
                    duration: 600,
                    smooth: true,
                  });
                }}
              >
                {event.host.name}
              </Button>
            </Flex>
          </Flex>

          {/* Event status */}
          <EventStatus event={event} />

          {/* Call to action buttons */}
          <Flex mt={{ base: '30px', md: '15px' }}>
            <Button
              leftIcon={<AddToCalendarIcon />}
              color={TEXT_COLOR}
              bg="none"
              border="1px solid #e5e5e5"
              width="100%"
              height="48px"
              borderRadius="10px"
              _hover={{
                bg: 'none',
                borderColor: TEXT_COLOR,
              }}
              as={'a'}
              href={getGoogleCalendarUrl({
                text: event.name,
                startDate: fromUnixTime(event.startedAt),
                endDate: fromUnixTime(event.endedAt),
                // FIXME: add namespace `common:` will only get the i18n key
                description: t(
                  'Welcome to the event, click the link: {{link}} to enter the room when event start',
                  {
                    link: `${ORIGIN}/events/${event.id}`,
                    nsSeparator: '|',
                  }
                ),
              })}
              target="_blank"
            >
              {t('common:Add to Google Calendar')}
            </Button>

            <Popover closeOnBlur={true} placement="bottom-end">
              {({ isOpen }) => (
                <>
                  <PopoverTrigger>
                    <Button
                      leftIcon={<ShareIcon />}
                      color={TEXT_COLOR}
                      bg="none"
                      width="fit-content"
                      ml="15px"
                      height="48px"
                      border="1px solid #e5e5e5"
                      borderRadius="10px"
                      _hover={{
                        bg: 'none',
                        borderColor: TEXT_COLOR,
                      }}
                      px="4"
                      flexShrink={0}
                    >
                      {t('event:Share')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent display={isOpen ? 'block' : 'none'}>
                    <Copy value={`${ORIGIN}/events/${event.id}`} />
                  </PopoverContent>
                </>
              )}
            </Popover>
          </Flex>
        </Flex>
      </GridItem>
    </Grid>
  );
};

export default Hero;
